import { useCallback, useRef, useState, type ReactNode } from "react";
import type { BoxAnswer, BoxConfirmationRequiredBody } from "../../api/shopMode";
import { runWithBoxAnswers } from "../../utils/boxConfirmation";
import { BoxPickPrompt } from "./BoxPickPrompt";

type PromptBoxes = BoxConfirmationRequiredBody["boxes"];

/**
 * G-84 — wraps a consuming request so the "Is this from box X?" question
 * appears whenever the server asks it, and the request is sent again with the
 * answers. One hook, mounted by every surface that consumes stock, so the
 * question looks and behaves the same at the till, in the bouquet builder and
 * on the waste form.
 *
 *   const boxPrompt = useBoxPickPrompt();
 *   const result = await boxPrompt.run((boxAnswers) => api.sell({ ...payload, boxAnswers }));
 *   …
 *   {boxPrompt.element}
 */
export function useBoxPickPrompt(): {
  run<T>(call: (answers: BoxAnswer[]) => Promise<T>): Promise<T>;
  element: ReactNode;
} {
  const [pending, setPending] = useState<PromptBoxes | null>(null);
  const resolver = useRef<((answers: BoxAnswer[] | null) => void) | null>(null);

  const ask = useCallback(
    (boxes: PromptBoxes) =>
      new Promise<BoxAnswer[] | null>((resolve) => {
        resolver.current = resolve;
        setPending(boxes);
      }),
    [],
  );

  const settle = useCallback((answers: BoxAnswer[] | null) => {
    const resolve = resolver.current;
    resolver.current = null;
    setPending(null);
    resolve?.(answers);
  }, []);

  const run = useCallback(
    <T,>(call: (answers: BoxAnswer[]) => Promise<T>) => runWithBoxAnswers(call, ask),
    [ask],
  );

  const element = pending ? (
    <BoxPickPrompt boxes={pending} onAnswer={(a) => settle(a)} onCancel={() => settle(null)} />
  ) : null;

  return { run, element };
}
